import { useEffect, useRef, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PieChart } from "../../charts/PieChart";
import { stableEntityColor } from "../../charts/colors";
import { formatSEK0 } from "../../lib/format";
import type { CryptoOverview } from "../../types";

interface CoinResult {
  coinId: string;
  symbol: string;
  name: string;
  marketCapRank: number | null;
}

interface CryptoSectionProps {
  data: CryptoOverview | null;
  onRefresh: () => void;
}

export function CryptoSection({ data, onRefresh }: CryptoSectionProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<CoinResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<CoinResult | null>(null);
  const [quantity, setQuantity] = useState("");
  const [cost, setCost] = useState("");
  const [error, setError] = useState<string | null>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (query.trim().length < 2 || selected) {
      setResults([]);
      return;
    }
    setSearching(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(`/api/crypto/search?q=${encodeURIComponent(query.trim())}`);
        const data = await res.json();
        setResults(Array.isArray(data) ? data : []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [query, selected]);

  async function handleAdd() {
    if (!selected) return;
    const qty = Number(quantity.replace(",", "."));
    if (!Number.isFinite(qty) || qty <= 0) return;
    setError(null);

    const res = await fetch("/api/crypto", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        coinId: selected.coinId,
        symbol: selected.symbol,
        name: selected.name,
        quantity: qty,
        costBasis: cost ? Number(cost.replace(",", ".")) : undefined,
      }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => null);
      setError(data?.error ?? "Kunde inte lägga till innehavet");
      return;
    }

    setSelected(null);
    setQuery("");
    setQuantity("");
    setCost("");
    onRefresh();
  }

  async function handleDelete(id: string) {
    await fetch(`/api/crypto/${id}`, { method: "DELETE" });
    onRefresh();
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  const delta = data.totalCostBasis > 0 ? data.totalValue - data.totalCostBasis : null;

  const allocation = data.holdings
    .filter((h) => h.value > 0)
    .map((h) => ({
      label: `${h.name} (${h.symbol})`,
      value: Math.round(h.value),
      color: stableEntityColor(h.coinId),
      key: h.id,
    }));

  return (
    <div className="space-y-4 lg:space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Totalt värde</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold tabular-nums">{formatSEK0(data.totalValue)}</p>
            {data.pricesFetchedAt && (
              <p className="mt-1 text-xs text-muted-foreground">
                Priser {new Date(data.pricesFetchedAt).toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" })} · CoinGecko
              </p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Utveckling</CardTitle>
          </CardHeader>
          <CardContent>
            {delta !== null ? (
              <p
                className={`text-2xl font-bold tabular-nums ${delta >= 0 ? "text-success" : "text-destructive"}`}
              >
                {delta >= 0 ? "+" : ""}
                {formatSEK0(delta)}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">Ange anskaffningsvärde för utveckling</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Lägg till innehav</CardTitle>
          <CardDescription>
            Sök efter en kryptovaluta och ange hur mycket ni äger — priset hämtas
            automatiskt i SEK. Lägger du till samma valuta igen summeras antalet.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {!selected ? (
            <div className="relative max-w-md">
              <Input
                placeholder="Sök (t.ex. Bitcoin, ETH...)"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              {(results.length > 0 || searching) && query.trim().length >= 2 && (
                <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border bg-popover shadow-lg">
                  {searching && (
                    <div className="px-3 py-2 text-sm text-muted-foreground">Söker...</div>
                  )}
                  {results.map((coin) => (
                    <button
                      key={coin.coinId}
                      className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-muted"
                      onClick={() => setSelected(coin)}
                    >
                      <span>
                        {coin.name}{" "}
                        <span className="text-muted-foreground">({coin.symbol})</span>
                      </span>
                      {coin.marketCapRank && (
                        <span className="text-xs text-muted-foreground">#{coin.marketCapRank}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="grid gap-2 rounded-lg border p-3 sm:grid-cols-[1fr_1fr_1fr_auto]">
              <div className="flex items-center gap-2 text-sm font-medium">
                {selected.name} ({selected.symbol})
                <button
                  className="text-xs text-muted-foreground underline"
                  onClick={() => setSelected(null)}
                >
                  byt
                </button>
              </div>
              <Input
                placeholder="Antal (t.ex. 0,25)"
                inputMode="decimal"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                autoFocus
              />
              <Input
                placeholder="Anskaffningsvärde kr (valfritt)"
                inputMode="decimal"
                value={cost}
                onChange={(e) => setCost(e.target.value)}
              />
              <Button onClick={() => void handleAdd()} disabled={!quantity}>
                <Plus className="mr-1 h-4 w-4" /> Lägg till
              </Button>
            </div>
          )}
          {error && (
            <div className="rounded-md bg-destructive/10 px-4 py-2.5 text-sm text-destructive">
              {error}
            </div>
          )}
        </CardContent>
      </Card>

      {data.holdings.length > 0 && (
        <div className="grid gap-4 lg:grid-cols-2 lg:gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Innehav</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                {data.holdings.map((holding) => (
                  <div
                    key={holding.id}
                    className="group flex items-center gap-3 rounded-lg px-2 py-2 -mx-2 hover:bg-muted/50"
                  >
                    <div
                      className="h-3 w-3 shrink-0 rounded-sm"
                      style={{ backgroundColor: stableEntityColor(holding.coinId) }}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2">
                        <span className="truncate font-medium">{holding.name}</span>
                        <span className="text-xs text-muted-foreground">{holding.symbol}</span>
                      </div>
                      <div className="text-xs text-muted-foreground tabular-nums">
                        {holding.quantity.toLocaleString("sv-SE", { maximumFractionDigits: 8 })} st
                        {holding.priceSek !== null && ` · ${formatSEK0(holding.priceSek)}/st`}
                        {holding.priceSource === "cached" && " (senast kända pris)"}
                        {holding.priceSource === "manual" && " (manuellt pris)"}
                        {holding.priceSource === "none" && " · pris saknas"}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium tabular-nums">{formatSEK0(holding.value)}</div>
                      {holding.change24hPct !== undefined && (
                        <div
                          className={`text-xs tabular-nums ${
                            holding.change24hPct >= 0 ? "text-success" : "text-destructive"
                          }`}
                        >
                          {holding.change24hPct >= 0 ? "+" : ""}
                          {holding.change24hPct.toFixed(1)}% idag
                        </div>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100 max-lg:opacity-60"
                      onClick={() => void handleDelete(holding.id)}
                      aria-label={`Ta bort ${holding.name}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {allocation.length > 1 && (
            <Card>
              <CardHeader>
                <CardTitle>Fördelning</CardTitle>
              </CardHeader>
              <CardContent>
                <PieChart data={allocation} />
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
